'use client';

import { useEffect } from 'react';
import { identifyKlaviyoProfile, identifyPostHogProfile } from '@/lib/analytics/client';

// Postscript's onsite SDK exposes subscriber events via a handful of public hooks
// depending on SDK version. We attach to every known pattern so newsletter-style
// signups via the Postscript popup get bridged into the Klaviyo anonymous profile,
// backfilling the browsing/cart events that already fired in this session.
interface PostscriptSdk {
	events?: {
		on?: (
			event: string,
			listener: (payload: PostscriptSubscribePayload) => void,
		) => (() => void) | undefined;
	};
	on?: (event: string, listener: (payload: PostscriptSubscribePayload) => void) => void;
}

interface PostscriptSubscribePayload {
	email?: unknown;
	phone?: unknown;
	phone_number?: unknown;
	first_name?: unknown;
	last_name?: unknown;
	subscriber?: {
		email?: unknown;
		phone?: unknown;
		phone_number?: unknown;
		first_name?: unknown;
		last_name?: unknown;
	} | null;
}

declare global {
	interface Window {
		postscript?: PostscriptSdk;
	}

	interface WindowEventMap {
		'postscript:subscribe': CustomEvent<PostscriptSubscribePayload>;
		'postscript:opt_in': CustomEvent<PostscriptSubscribePayload>;
	}
}

const POSTSCRIPT_SDK_POLL_INTERVAL_MS = 500;
const POSTSCRIPT_SDK_POLL_TIMEOUT_MS = 30_000;
const PS_SUBSCRIBE_EVENTS = ['subscribe', 'opt_in', 'optin', 'subscriber:created'];

function normalizePostscriptString(value: unknown) {
	if (typeof value !== 'string') return undefined;
	const trimmed = value.trim();
	return trimmed || undefined;
}

function extractIdentifiers(payload: PostscriptSubscribePayload | undefined | null) {
	if (!payload) return null;
	const source = payload.subscriber ?? payload;
	const email = normalizePostscriptString(source.email)?.toLowerCase();
	const phone = normalizePostscriptString(source.phone ?? source.phone_number);
	const firstName = normalizePostscriptString(source.first_name);
	const lastName = normalizePostscriptString(source.last_name);

	if (!email && !phone) return null;
	return { email, phone, firstName, lastName };
}

function identifyFromPostscriptPayload(payload: PostscriptSubscribePayload | undefined | null) {
	const identifiers = extractIdentifiers(payload);
	if (!identifiers) return;

	identifyKlaviyoProfile({
		email: identifiers.email,
		phone: identifiers.phone,
		firstName: identifiers.firstName,
		lastName: identifiers.lastName,
		explicitOptIn: true,
	});
	identifyPostHogProfile({
		email: identifiers.email,
		phone: identifiers.phone,
		firstName: identifiers.firstName,
		lastName: identifiers.lastName,
		source: 'postscript_signup',
	});
}

function attachToPostscriptSdk(sdk: PostscriptSdk, disposers: Array<() => void>) {
	for (const eventName of PS_SUBSCRIBE_EVENTS) {
		if (typeof sdk.events?.on === 'function') {
			const dispose = sdk.events.on(eventName, identifyFromPostscriptPayload);
			if (typeof dispose === 'function') disposers.push(dispose);
		}
		if (typeof sdk.on === 'function') {
			sdk.on(eventName, identifyFromPostscriptPayload);
		}
	}
}

function pollForPostscriptSdk(disposers: Array<() => void>) {
	const start = Date.now();
	let cancelled = false;
	const timer = window.setInterval(() => {
		if (cancelled) {
			window.clearInterval(timer);
			return;
		}
		const next = window.postscript;
		if (next) {
			window.clearInterval(timer);
			attachToPostscriptSdk(next, disposers);
			return;
		}
		if (Date.now() - start > POSTSCRIPT_SDK_POLL_TIMEOUT_MS) {
			window.clearInterval(timer);
		}
	}, POSTSCRIPT_SDK_POLL_INTERVAL_MS);
	disposers.push(() => {
		cancelled = true;
		window.clearInterval(timer);
	});
}

function attachDomListeners(disposers: Array<() => void>) {
	const handler = (event: CustomEvent<PostscriptSubscribePayload>) => {
		identifyFromPostscriptPayload(event.detail);
	};
	window.addEventListener('postscript:subscribe', handler);
	window.addEventListener('postscript:opt_in', handler);
	disposers.push(() => {
		window.removeEventListener('postscript:subscribe', handler);
		window.removeEventListener('postscript:opt_in', handler);
	});
}

export function PostscriptKlaviyoBridge() {
	useEffect(() => {
		const disposers: Array<() => void> = [];

		const sdk = window.postscript;
		if (sdk) {
			attachToPostscriptSdk(sdk, disposers);
		} else {
			pollForPostscriptSdk(disposers);
		}

		attachDomListeners(disposers);

		return () => {
			for (const dispose of disposers) dispose();
		};
	}, []);

	return null;
}
